import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../api";
import { useStore } from "../store";

const STATUS_ORDER = ["ricevuto", "confermato", "in preparazione", "spedito", "consegnato"];

export default function MyOrders() {
  const navigate = useNavigate();
  const { seenOrders, markOrdersSeen } = useStore();
  const [email, setEmail] = useState(() => localStorage.getItem("dorae_customer_email") || "");
  const [orders, setOrders] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchOrders = async (em) => {
    setLoading(true);
    try {
      const { data } = await api.get("/my-orders", { params: { email: em } });
      setOrders(data.orders);
      setLoaded(true);
    } catch {
      setOrders([]);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    localStorage.setItem("dorae_customer_email", email.trim().toLowerCase());
    fetchOrders(email.trim().toLowerCase());
  };

  useEffect(() => {
    if (email && localStorage.getItem("dorae_customer_email")) {
      fetchOrders(email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loaded) {
      markOrdersSeen(orders.map((o) => `${o.id}:${o.status}`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  const unseenCount = orders.filter((o) => !seenOrders.includes(`${o.id}:${o.status}`)).length;

  return (
    <div className="max-w-2xl mx-auto px-4 pb-24" data-testid="my-orders-page">
      <div className="flex items-center gap-3 pt-5 pb-2">
        <button data-testid="back-button" onClick={() => navigate(-1)} aria-label="Torna indietro" className="p-2 -ml-2 text-[#2C2C2C] hover:opacity-60 transition-opacity duration-200">
          <ArrowLeft size={20} strokeWidth={1.5} />
        </button>
      </div>
      <div className="text-center mb-8">
        <h1 className="font-display text-3xl text-[#2C2C2C]" data-testid="my-orders-title">I miei ordini</h1>
        <div className="gold-line w-24 mx-auto mt-4" />
      </div>

      {!loaded ? (
        <form onSubmit={submit} className="max-w-sm mx-auto" data-testid="orders-lookup-form">
          <p className="text-sm text-center text-[#6B6357] tracking-wide mb-5">Inserisci la tua email per vedere i tuoi ordini</p>
          <input required type="email" data-testid="orders-email" placeholder="La tua email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-white border border-[#EAE4DA] px-4 py-3 text-base outline-none focus:border-[#D4AF37] transition-colors" />
          <button type="submit" data-testid="orders-lookup-submit" className="mt-4 w-full gold-gradient text-white py-3.5 text-[12px] font-semibold tracking-wide-spaced uppercase">
            Vedi i miei ordini
          </button>
        </form>
      ) : (
        <>
          {unseenCount > 0 && (
            <p className="text-center text-xs tracking-wide uppercase text-[#AA7C11] mb-5" data-testid="orders-updated-tag">
              {unseenCount === 1 ? "1 ordine aggiornato" : `${unseenCount} ordini aggiornati`}
            </p>
          )}
          {loading && <p className="text-center text-sm text-[#8B8578] py-8 animate-pulse">Caricamento...</p>}
          {!loading && !orders.length && (
            <p className="text-center text-sm tracking-wide text-[#8B8578] py-14" data-testid="orders-empty">Nessun ordine per questa email.</p>
          )}
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} className="bg-white border border-[#EAE4DA] p-5" data-testid={`order-${o.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <p className="text-xs text-[#8B8578] tracking-wide uppercase">Ordine del {new Date(o.created_at).toLocaleDateString("it-IT")}</p>
                    <p className="text-sm font-semibold gold-gradient-text mt-1">€ {o.total.toFixed(2)}</p>
                  </div>
                  <span className="text-[11px] font-semibold tracking-wide-spaced uppercase gold-gradient text-white px-3 py-1.5" data-testid={`status-${o.id}`}>
                    {o.status}
                  </span>
                </div>
                <ul className="mt-3 pt-3 border-t border-[#EDE6DB] space-y-1">
                  {o.items.map((i) => (
                    <li key={i.product_id} className="text-xs text-[#6B6357] flex justify-between">
                      <span>{i.quantity} × {i.name}</span>
                      <span>€ {(i.price * i.quantity).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex items-center gap-1.5" data-testid={`steps-${o.id}`}>
                  {STATUS_ORDER.map((s, i) => {
                    const active = STATUS_ORDER.indexOf(o.status) >= i;
                    return (
                      <div key={s} className="flex-1">
                        <div className={`h-1 ${active ? "gold-gradient" : "bg-[#EDE6DB]"}`} />
                        <p className={`mt-1.5 text-[8px] tracking-wide uppercase text-center ${active ? "text-[#AA7C11] font-semibold" : "text-[#B0A898]"}`}>{s}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => { localStorage.removeItem("dorae_customer_email"); setLoaded(false); setOrders([]); }} className="mt-8 mx-auto block text-[11px] tracking-wide-spaced uppercase text-[#8B8578] underline underline-offset-4" data-testid="orders-change-email">
            Cambia email
          </button>
        </>
      )}
    </div>
  );
}
